import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-scanner (brief "Leitura de placa e VIN pela câmera", CEO 2026-10-04) —
 * câmera ao vivo DENTRO da página (sem instalar nada; iPhone e Android
 * iguais). A cada ~800 ms recorta só a MOLDURA, deixa em cinza/alto
 * contraste e manda ao servidor (`scanner.read`), que faz o OCR e casa com
 * os carros do Gestion. Aceita quando a MESMA leitura vem 3 vezes entre as
 * últimas 5 ("o número trava na tela").
 *
 * Conveniência, nunca requisito: "Digitar" sempre visível; permissão negada
 * ou câmera ausente → volta ao campo. Ao sair, a câmera é DESLIGADA.
 */

export type ScanModo = "plaque" | "vin";
export interface ScanCandidato {
  dossierId: number;
  dossierCode: string | null;
  placa: string | null;
  vin: string | null;
  veiculo: string | null;
  distancia: number;
  conflito: { doVin: string; doDevis: string; wmi: string } | null;
}

/** Câmera ao vivo disponível? (HTTPS + getUserMedia). Senão: foto ou digitação. */
export function cameraAoVivoDisponivel(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

const INTERVALO_MS = 800;
const VOTOS = 5;
const PRECISA = 3;
const LARGURA = 800;

type Fase = "iniciando" | "lendo" | "aceito" | "negado" | "indisponivel";

export function CameraScanner({
  modo,
  companyId,
  chantierId,
  onResult,
  onClose,
}: {
  modo: ScanModo;
  companyId: number;
  chantierId?: number | null;
  onResult: (texto: string, candidato: ScanCandidato | null) => void;
  /** Fechar / "Digitar": volta para o campo de texto. */
  onClose: () => void;
}) {
  const t = useT();
  const ler = trpc.scanner.read.useMutation();
  const lerRef = useRef(ler.mutateAsync);
  lerRef.current = ler.mutateAsync;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const caixaRef = useRef<HTMLDivElement | null>(null);
  const molduraRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const vivoRef = useRef(true);
  const faseRef = useRef<Fase>("iniciando");
  const votosRef = useRef<string[]>([]);

  const [fase, setFaseState] = useState<Fase>("iniciando");
  const setFase = (f: Fase) => {
    faseRef.current = f;
    setFaseState(f);
  };
  const [semRede, setSemRede] = useState(false);
  const [lanterna, setLanterna] = useState<{ suportada: boolean; ligada: boolean }>({
    suportada: false,
    ligada: false,
  });
  /** Última leitura (feedback ao vivo, pequeno). */
  const [ultima, setUltima] = useState<string | null>(null);
  /** Leitura estável (3/5): com candidatos para o OK, ou fora do Gestion. */
  const [estavel, setEstavel] = useState<{ texto: string; candidatos: ScanCandidato[] } | null>(
    null,
  );

  // ── recorte da moldura → cinza, contraste, binarização ─────────────────────
  const capturar = useCallback((): string | null => {
    const v = videoRef.current;
    const caixa = caixaRef.current;
    const mold = molduraRef.current;
    if (!v || !caixa || !mold || !v.videoWidth) return null;
    const cr = caixa.getBoundingClientRect();
    const fr = mold.getBoundingClientRect();
    const vw = v.videoWidth;
    const vh = v.videoHeight;
    // object-fit: cover → escala e deslocamento do vídeo dentro da caixa.
    const esc = Math.max(cr.width / vw, cr.height / vh);
    const offX = (cr.width - vw * esc) / 2;
    const offY = (cr.height - vh * esc) / 2;
    let sx = (fr.left - cr.left - offX) / esc;
    let sy = (fr.top - cr.top - offY) / esc;
    let sw = fr.width / esc;
    let sh = fr.height / esc;
    sx = Math.max(0, sx);
    sy = Math.max(0, sy);
    sw = Math.min(sw, vw - sx);
    sh = Math.min(sh, vh - sy);
    if (sw < 10 || sh < 5) return null;

    const margem = 16;
    const W = LARGURA;
    const H = Math.max(24, Math.round((W * sh) / sw));
    const canvas = canvasRef.current ?? document.createElement("canvas");
    canvasRef.current = canvas;
    canvas.width = W + margem * 2;
    canvas.height = H + margem * 2;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(v, sx, sy, sw, sh, margem, margem, W, H);
    binarizar(ctx, canvas.width, canvas.height);
    let url = canvas.toDataURL("image/png");
    if (url.length > 270_000) url = canvas.toDataURL("image/jpeg", 0.7);
    return url;
  }, []);

  const parar = useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const tick = useCallback(async () => {
    if (!vivoRef.current || faseRef.current !== "lendo") return;
    const inicio = performance.now();
    const imagem = capturar();
    if (imagem) {
      try {
        const r = await lerRef.current({
          companyId,
          modo,
          image: imagem,
          ...(chantierId ? { chantierId } : {}),
        });
        if (!vivoRef.current) return;
        setSemRede(false);
        if (r.texto) {
          setUltima(r.texto);
          const votos = [...votosRef.current, r.texto].slice(-VOTOS);
          votosRef.current = votos;
          if (votos.filter((x) => x === r.texto).length >= PRECISA) {
            const candidatos = r.candidatos as ScanCandidato[];
            setEstavel({ texto: r.texto, candidatos });
            if (candidatos.length > 0) {
              // Achou carro(s) do Gestion: trava, vibra e espera o toque.
              setFase("aceito");
              try {
                navigator.vibrate?.(50);
              } catch {
                /* sem vibração */
              }
              return;
            }
            // Fora do Gestion: continua lendo (brief critério 5), mas o
            // texto estável fica oferecido em "Usar mesmo assim".
          }
        }
      } catch (e) {
        if (!vivoRef.current) return;
        const transporte = !(e as { data?: unknown })?.data || !navigator.onLine;
        if (transporte) setSemRede(true);
      }
    }
    if (!vivoRef.current || faseRef.current !== "lendo") return;
    const passou = performance.now() - inicio;
    timerRef.current = window.setTimeout(() => void tick(), Math.max(0, INTERVALO_MS - passou));
  }, [capturar, chantierId, companyId, modo]);

  // ── câmera: liga ao abrir, DESLIGA ao sair ─────────────────────────────────
  useEffect(() => {
    vivoRef.current = true;
    if (!cameraAoVivoDisponivel()) {
      setFase("indisponivel");
      return;
    }
    // Cada abertura cuida da PRÓPRIA câmera: se o componente sair (ou o React
    // remontar em dev) enquanto o aparelho ainda abre a câmera, o stream que
    // chegar depois é desligado na hora — nunca fica uma câmera órfã ligada.
    let ativo = true;
    let meuStream: MediaStream | null = null;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!ativo) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        meuStream = stream;
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => undefined);
        }
        const track = stream.getVideoTracks()[0];
        const caps = (track?.getCapabilities?.() ?? {}) as { torch?: boolean };
        if (caps.torch) {
          let ligada = false;
          // VIN no batente fica na sombra: tenta ligar a lanterna sozinho.
          if (modo === "vin") {
            try {
              await track!.applyConstraints({
                advanced: [{ torch: true } as MediaTrackConstraintSet],
              });
              ligada = true;
            } catch {
              ligada = false;
            }
          }
          if (!ativo) return;
          setLanterna({ suportada: true, ligada });
        }
        // Saiu enquanto a câmera abria: não começa um laço de leitura órfão.
        if (!ativo) return;
        setFase("lendo");
        void tick();
      } catch (e) {
        if (!ativo) return;
        const nome = (e as { name?: string })?.name;
        setFase(nome === "NotAllowedError" || nome === "SecurityError" ? "negado" : "indisponivel");
      }
    })();
    return () => {
      ativo = false;
      vivoRef.current = false;
      parar();
      meuStream?.getTracks().forEach((tr) => tr.stop());
      if (streamRef.current === meuStream) streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    // Só o modo reinicia a câmera (religar a cada render drenaria a bateria).
  }, [modo]);

  async function alternarLanterna() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const nova = !lanterna.ligada;
    try {
      await track.applyConstraints({ advanced: [{ torch: nova } as MediaTrackConstraintSet] });
      setLanterna({ suportada: true, ligada: nova });
    } catch {
      /* aparelho recusou: segue sem lanterna, sem erro visível */
    }
  }

  function lerDeNovo() {
    votosRef.current = [];
    setEstavel(null);
    setUltima(null);
    setFase("lendo");
    void tick();
  }

  const escolher = (c: ScanCandidato) =>
    onResult((modo === "vin" ? c.vin : c.placa) ?? estavel?.texto ?? "", c);

  const proporcao = modo === "plaque" ? "4.7 / 1" : "12 / 1";
  const largura = modo === "plaque" ? "min(80vw, 520px)" : "min(92vw, 680px)";
  const titulo = modo === "plaque" ? t("techPortal.camTitlePlate") : "VIN";
  const candidatos = estavel?.candidatos ?? [];

  return (
    <div
      className="fixed inset-x-0 top-0 z-[60] flex flex-col bg-black text-white"
      style={{ height: "100dvh" }}
      role="dialog"
      aria-modal
    >
      <div ref={caixaRef} className="relative min-h-0 flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Moldura guia: o escurecimento fora dela enquadra sem instrução. */}
        {(fase === "lendo" || fase === "aceito" || fase === "iniciando") && (
          <div
            ref={molduraRef}
            className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 rounded-md border-2 border-white"
            style={{
              width: largura,
              aspectRatio: proporcao,
              boxShadow: "0 0 0 200vmax rgba(0,0,0,0.5)",
            }}
          />
        )}

        {/* Barra do topo */}
        <div
          className="absolute inset-x-0 top-0 flex items-center justify-between gap-2 px-3"
          style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t("techPortal.camClose")}
            className="rounded-full bg-black/60 px-3 py-2 text-lg"
          >
            ✕
          </button>
          <span className="rounded-full bg-black/60 px-3 py-1 text-sm font-semibold">{titulo}</span>
          {lanterna.suportada ? (
            <button
              type="button"
              onClick={() => void alternarLanterna()}
              aria-pressed={lanterna.ligada}
              aria-label={t("techPortal.camTorch")}
              className={`rounded-full px-3 py-2 text-lg ${lanterna.ligada ? "bg-amber-400 text-black" : "bg-black/60"}`}
            >
              🔦
            </button>
          ) : (
            <span className="w-11" />
          )}
        </div>

        {/* Estado logo abaixo da moldura */}
        <div className="absolute inset-x-0 top-[52%] flex flex-col items-center gap-2 px-4 text-center">
          {fase === "iniciando" && (
            <p className="text-sm text-white/80">{t("techPortal.camStarting")}</p>
          )}
          {fase === "lendo" && (
            <p className="flex items-center gap-2 text-sm text-white/80">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-lime" />
              {modo === "plaque" ? t("techPortal.camAimPlate") : t("techPortal.camAimVin")}
            </p>
          )}
          {fase === "lendo" && ultima && !estavel && (
            <p className="font-mono text-base tracking-wider text-white/60">{ultima}</p>
          )}
          {semRede && (
            <p className="rounded-md bg-amber-500/90 px-3 py-1.5 text-xs font-semibold text-black">
              {t("techPortal.camOffline")}
            </p>
          )}
          {(fase === "negado" || fase === "indisponivel") && (
            <p className="max-w-sm rounded-lg bg-neutral-900/95 px-4 py-3 text-sm text-neutral-100">
              {fase === "negado" ? t("techPortal.camDenied") : t("techPortal.camUnavailable")}
            </p>
          )}
        </div>
      </div>

      {/* Painel de baixo: resultado + "Digitar" sempre visível */}
      <div
        className="space-y-3 bg-neutral-950 px-4 pt-3"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
      >
        {estavel && (
          <div className="space-y-2">
            <p className="text-center font-mono text-3xl font-bold tracking-[0.12em]">
              {estavel.texto}
            </p>
            {candidatos.length === 0 && (
              <div className="flex flex-col items-center gap-2">
                <p className="text-center text-sm text-amber-300">{t("techPortal.camNotFound")}</p>
                <button
                  type="button"
                  onClick={() => onResult(estavel.texto, null)}
                  className="rounded-lg border border-neutral-600 px-4 py-2 text-sm"
                >
                  {t("techPortal.camUseAnyway")}
                </button>
              </div>
            )}
            {candidatos.length === 1 && candidatos[0] && (
              <div className="space-y-2">
                <p className="text-center text-sm text-neutral-300">
                  {[candidatos[0].veiculo, candidatos[0].dossierCode].filter(Boolean).join(" · ")}
                </p>
                {candidatos[0].conflito ? (
                  <p className="rounded-md bg-red-600/20 px-3 py-2 text-center text-sm text-red-200">
                    {t("techPortal.camConflictPre")} {candidatos[0].conflito.doVin} (
                    {candidatos[0].conflito.wmi}) · {t("techPortal.camConflictDevis")}{" "}
                    {candidatos[0].conflito.doDevis}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => escolher(candidatos[0]!)}
                    className="w-full rounded-xl bg-lime py-4 text-xl font-bold text-black"
                  >
                    OK
                  </button>
                )}
              </div>
            )}
            {candidatos.length > 1 && (
              <div className="space-y-2">
                <p className="text-center text-sm text-neutral-300">{t("techPortal.camPickOne")}</p>
                {candidatos.map((c) => (
                  <button
                    key={c.dossierId}
                    type="button"
                    disabled={c.conflito != null}
                    onClick={() => escolher(c)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-neutral-700 px-3 py-3 text-left disabled:opacity-40"
                  >
                    <span className="font-mono text-base font-semibold">
                      {modo === "vin" ? c.vin : c.placa}
                    </span>
                    <span className="truncate text-xs text-neutral-400">
                      {[c.veiculo, c.dossierCode].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {fase === "aceito" && (
              <button
                type="button"
                onClick={lerDeNovo}
                className="w-full rounded-lg border border-neutral-700 py-2 text-sm text-neutral-300"
              >
                {t("techPortal.camRetry")}
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl border border-neutral-600 py-3 text-base font-semibold"
        >
          ⌨ {t("techPortal.camType")}
        </button>
      </div>
    </div>
  );
}

/**
 * Cinza → contraste (percentis 2/98) → binarização ADAPTATIVA (Bradley, por
 * imagem integral). Placa suja ou VIN com reflexo ganham mais com isto do
 * que com troca de motor de OCR. Texto claro em fundo escuro (VIN gravado)
 * é invertido: o Tesseract quer texto escuro.
 */
function binarizar(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const n = w * h;
  const cinza = new Uint8ClampedArray(n);
  const hist = new Uint32Array(256);
  for (let i = 0; i < n; i++) {
    const g = (d[i * 4]! * 299 + d[i * 4 + 1]! * 587 + d[i * 4 + 2]! * 114) / 1000;
    cinza[i] = g;
    hist[cinza[i]!]!++;
  }
  let acc = 0;
  let lo = 0;
  let hi = 255;
  for (let v = 0; v < 256; v++) {
    acc += hist[v]!;
    if (acc >= n * 0.02) {
      lo = v;
      break;
    }
  }
  acc = 0;
  for (let v = 255; v >= 0; v--) {
    acc += hist[v]!;
    if (acc >= n * 0.02) {
      hi = v;
      break;
    }
  }
  const faixa = Math.max(1, hi - lo);
  for (let i = 0; i < n; i++) cinza[i] = ((cinza[i]! - lo) * 255) / faixa;

  const integral = new Float64Array((w + 1) * (h + 1));
  for (let y = 1; y <= h; y++) {
    let linha = 0;
    for (let x = 1; x <= w; x++) {
      linha += cinza[(y - 1) * w + (x - 1)]!;
      integral[y * (w + 1) + x] = integral[(y - 1) * (w + 1) + x]! + linha;
    }
  }
  const s = Math.max(8, Math.floor(w / 16));
  const meio = Math.floor(s / 2);
  const tFator = 0.15;
  const saida = new Uint8ClampedArray(n);
  let pretos = 0;
  for (let y = 0; y < h; y++) {
    const y1 = Math.max(0, y - meio);
    const y2 = Math.min(h - 1, y + meio);
    for (let x = 0; x < w; x++) {
      const x1 = Math.max(0, x - meio);
      const x2 = Math.min(w - 1, x + meio);
      const cont = (x2 - x1 + 1) * (y2 - y1 + 1);
      const soma =
        integral[(y2 + 1) * (w + 1) + (x2 + 1)]! -
        integral[y1 * (w + 1) + (x2 + 1)]! -
        integral[(y2 + 1) * (w + 1) + x1]! +
        integral[y1 * (w + 1) + x1]!;
      const preto = cinza[y * w + x]! * cont <= soma * (1 - tFator);
      saida[y * w + x] = preto ? 0 : 255;
      if (preto) pretos++;
    }
  }
  if (pretos > n * 0.55) for (let i = 0; i < n; i++) saida[i] = 255 - saida[i]!;
  limparManchasGrandes(saida, w, h);
  for (let i = 0; i < n; i++) {
    const v = saida[i]!;
    d[i * 4] = v;
    d[i * 4 + 1] = v;
    d[i * 4 + 2] = v;
    d[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Tira o que é preto e grande demais para ser letra: fundo escuro em volta
 * da placa (lataria), a faixa azul do "F", a borda da placa. O Tesseract em
 * modo "linha única" se perde com esses blocos (teste com a câmera simulada:
 * a placa nítida voltava vazia). Componentes conexos de pixels pretos com
 * altura > 85 % do recorte, largura > 30 % ou área > 8 % viram branco.
 */
function limparManchasGrandes(px: Uint8ClampedArray, w: number, h: number) {
  const visto = new Uint8Array(w * h);
  const pilha = new Int32Array(w * h);
  for (let i0 = 0; i0 < w * h; i0++) {
    if (px[i0] !== 0 || visto[i0]) continue;
    let topo = 0;
    pilha[topo++] = i0;
    visto[i0] = 1;
    const membros: number[] = [];
    let x0 = w;
    let x1 = 0;
    let y0 = h;
    let y1 = 0;
    while (topo > 0) {
      const i = pilha[--topo]!;
      membros.push(i);
      const x = i % w;
      const y = (i - x) / w;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      const viz = [
        x > 0 ? i - 1 : -1,
        x < w - 1 ? i + 1 : -1,
        y > 0 ? i - w : -1,
        y < h - 1 ? i + w : -1,
      ];
      for (const j of viz) {
        if (j >= 0 && px[j] === 0 && !visto[j]) {
          visto[j] = 1;
          pilha[topo++] = j;
        }
      }
    }
    const grande = y1 - y0 + 1 > h * 0.85 || x1 - x0 + 1 > w * 0.3 || membros.length > w * h * 0.08;
    if (grande) for (const i of membros) px[i] = 255;
  }
}
